// MobileSearchModal - Fullscreen search experience for mobile with location autocomplete

import React, { useState, useEffect, useRef } from 'react';
import { Search, X, Clock, TrendingUp, Navigation, Loader2 } from 'lucide-react';
import { LocationAutocomplete } from '../location/LocationAutocomplete';
import type { GeocodedLocation } from '../../types';

interface RecentSearch {
  query: string;
  timestamp: number;
}

interface MobileSearchModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSearch: (query: string) => void;
  onLocationSelect?: (location: GeocodedLocation) => void;
  onNearbyClick?: () => void;
  initialQuery?: string;
  trendingSearches?: string[];
  isLocating?: boolean;
  nearbyAddress?: string;
}

const STORAGE_KEY = 'goodplace_recent_searches';
const MAX_RECENT_SEARCHES = 5;

// Default trending searches
const DEFAULT_TRENDING = [
  'Kaffee',
  'Ruhig arbeiten',
  'Biergarten',
  'Live Musik',
  'Gemuetlich',
];

export function MobileSearchModal({
  isOpen,
  onClose,
  onSearch,
  onLocationSelect,
  onNearbyClick,
  initialQuery = '',
  trendingSearches = DEFAULT_TRENDING,
  isLocating = false,
  nearbyAddress,
}: MobileSearchModalProps) {
  const [query, setQuery] = useState(initialQuery);
  const [recentSearches, setRecentSearches] = useState<RecentSearch[]>([]);
  const [activeTab, setActiveTab] = useState<'was' | 'wo'>('was');
  const [locationQuery, setLocationQuery] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);

  // Load recent searches from localStorage
  useEffect(() => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (stored) {
        setRecentSearches(JSON.parse(stored));
      }
    } catch {
      // Ignore localStorage errors
    }
  }, []);

  // Focus input when modal opens
  useEffect(() => {
    if (isOpen && inputRef.current && activeTab === 'was') {
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  }, [isOpen, activeTab]);

  // Reset query when modal opens with new initial value
  useEffect(() => {
    if (isOpen) {
      setQuery(initialQuery);
    }
  }, [isOpen, initialQuery]);

  const saveSearch = (searchQuery: string) => {
    if (!searchQuery.trim()) return;

    const newSearch: RecentSearch = {
      query: searchQuery.trim(),
      timestamp: Date.now(),
    };

    const updated = [
      newSearch,
      ...recentSearches.filter((s) => s.query !== newSearch.query),
    ].slice(0, MAX_RECENT_SEARCHES);

    setRecentSearches(updated);

    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(updated));
    } catch {
      // Ignore localStorage errors
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (query.trim()) {
      saveSearch(query);
      onSearch(query.trim());
      onClose();
    }
  };

  const handleQuickSearch = (searchQuery: string) => {
    setQuery(searchQuery);
    saveSearch(searchQuery);
    onSearch(searchQuery);
    onClose();
  };

  const handleLocationSelect = (location: GeocodedLocation) => {
    if (onLocationSelect) {
      onLocationSelect(location);
    }
    onClose();
  };

  const handleNearbyClick = () => {
    if (onNearbyClick) {
      onNearbyClick();
    }
    onClose();
  };

  const clearRecentSearches = () => {
    setRecentSearches([]);
    try {
      localStorage.removeItem(STORAGE_KEY);
    } catch {
      // Ignore
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 bg-white animate-fade-in">
      {/* Header */}
      <div className="sticky top-0 bg-white border-b border-gray-100 px-4 py-3">
        <div className="flex items-center gap-3 mb-3">
          <button
            type="button"
            onClick={onClose}
            className="p-2 -ml-2 hover:bg-gray-100 rounded-full transition"
          >
            <X className="w-5 h-5 text-gray-600" />
          </button>
          <h2 className="text-lg font-semibold text-gray-900">Suchen</h2>
        </div>

        {/* Tab Switcher */}
        <div className="flex gap-2">
          <button
            onClick={() => setActiveTab('was')}
            className={`flex-1 py-2 px-4 rounded-full text-sm font-medium transition ${
              activeTab === 'was'
                ? 'bg-gray-900 text-white'
                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
            }`}
          >
            Was suchst du?
          </button>
          <button
            onClick={() => setActiveTab('wo')}
            className={`flex-1 py-2 px-4 rounded-full text-sm font-medium transition ${
              activeTab === 'wo'
                ? 'bg-gray-900 text-white'
                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
            }`}
          >
            Wo?
          </button>
        </div>
      </div>

      {/* Content */}
      <div className="px-4 py-6 overflow-y-auto" style={{ maxHeight: 'calc(100vh - 120px)' }}>
        {activeTab === 'was' ? (
          <>
            {/* Search Input */}
            <form onSubmit={handleSubmit} className="mb-6">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                <input
                  ref={inputRef}
                  type="text"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder="Cafe, Spieleabend, Stammtisch..."
                  className="w-full pl-10 pr-4 py-3 bg-gray-100 rounded-xl text-gray-900 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-primary/20"
                />
              </div>
              {query && (
                <button
                  type="submit"
                  className="w-full mt-3 px-4 py-3 bg-indigo-500 text-white rounded-xl font-medium hover:bg-indigo-600 transition"
                >
                  Suchen
                </button>
              )}
            </form>

            {/* Recent Searches */}
            {recentSearches.length > 0 && (
              <div className="mb-8">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wide flex items-center gap-2">
                    <Clock className="w-4 h-4" />
                    Letzte Suchen
                  </h3>
                  <button
                    onClick={clearRecentSearches}
                    className="text-xs text-gray-400 hover:text-gray-600"
                  >
                    Loeschen
                  </button>
                </div>

                <div className="space-y-1">
                  {recentSearches.map((search) => (
                    <button
                      key={search.timestamp}
                      onClick={() => handleQuickSearch(search.query)}
                      className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg hover:bg-gray-50 transition text-left"
                    >
                      <Clock className="w-4 h-4 text-gray-400" />
                      <span className="text-gray-700">{search.query}</span>
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Trending Searches */}
            <div>
              <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wide flex items-center gap-2 mb-3">
                <TrendingUp className="w-4 h-4" />
                Beliebt gerade
              </h3>

              <div className="flex flex-wrap gap-2">
                {trendingSearches.map((term) => (
                  <button
                    key={term}
                    onClick={() => handleQuickSearch(term)}
                    className="px-4 py-2 bg-gray-100 rounded-full text-sm text-gray-700 hover:bg-gray-200 transition"
                  >
                    {term}
                  </button>
                ))}
              </div>
            </div>
          </>
        ) : (
          <>
            {/* Nearby Button */}
            <button
              onClick={handleNearbyClick}
              disabled={isLocating}
              className="w-full flex items-center gap-4 p-4 mb-4 bg-indigo-50 rounded-xl hover:bg-indigo-100 transition disabled:opacity-50"
            >
              <div className="w-12 h-12 rounded-full bg-indigo-100 flex items-center justify-center flex-shrink-0">
                {isLocating ? (
                  <Loader2 className="w-6 h-6 text-indigo-600 animate-spin" />
                ) : (
                  <Navigation className="w-6 h-6 text-indigo-600" />
                )}
              </div>
              <div className="flex-1 text-left">
                <div className="font-semibold text-gray-900">In meiner Naehe</div>
                <div className="text-sm text-gray-500">
                  {isLocating
                    ? 'Standort wird ermittelt...'
                    : nearbyAddress || 'Aktuellen Standort verwenden'}
                </div>
              </div>
            </button>

            {/* Location Autocomplete with Text Fallback */}
            <div className="mb-6">
              <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-3">
                Ort suchen
              </h3>
              <LocationAutocomplete
                value={locationQuery}
                onChange={setLocationQuery}
                onSelect={handleLocationSelect}
                placeholder="Stadt, Stadtteil oder Region..."
                showNearbyButton={false}
              />
              {/* Direct text search button when text is entered */}
              {locationQuery.trim().length >= 2 && (
                <button
                  onClick={() => {
                    const location: GeocodedLocation = {
                      latitude: 0,
                      longitude: 0,
                      formattedAddress: locationQuery.trim(),
                      city: locationQuery.trim(),
                    };
                    handleLocationSelect(location);
                  }}
                  className="w-full mt-2 flex items-center gap-3 p-3 bg-indigo-50 rounded-xl hover:bg-indigo-100 transition text-left"
                >
                  <Search className="w-5 h-5 text-indigo-600" />
                  <span className="text-indigo-700 font-medium">
                    Orte in "{locationQuery.trim()}" suchen
                  </span>
                </button>
              )}
            </div>

            {/* Popular Locations - Extended list */}
            <div>
              <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-3">
                Beliebte Orte
              </h3>
              <div className="space-y-2">
                {[
                  { name: 'Muenchen', desc: 'Bayern' },
                  { name: 'Berlin', desc: 'Berlin' },
                  { name: 'Hamburg', desc: 'Hamburg' },
                  { name: 'Koeln', desc: 'Nordrhein-Westfalen' },
                  { name: 'Frankfurt', desc: 'Hessen' },
                  { name: 'Garmisch-Partenkirchen', desc: 'Bayern' },
                  { name: 'Donauwoerth', desc: 'Bayern' },
                  { name: 'Augsburg', desc: 'Bayern' },
                  { name: 'Nuernberg', desc: 'Bayern' },
                  { name: 'Stuttgart', desc: 'Baden-Wuerttemberg' },
                ].map((city) => (
                  <button
                    key={city.name}
                    onClick={() => {
                      const location: GeocodedLocation = {
                        latitude: 0,
                        longitude: 0,
                        formattedAddress: `${city.name}, ${city.desc}`,
                        city: city.name,
                      };
                      handleLocationSelect(location);
                    }}
                    className="w-full flex items-center gap-3 p-3 rounded-xl hover:bg-gray-50 transition text-left"
                  >
                    <div className="w-10 h-10 rounded-full bg-gray-100 flex items-center justify-center">
                      <Search className="w-5 h-5 text-gray-500" />
                    </div>
                    <div>
                      <div className="font-medium text-gray-900">{city.name}</div>
                      <div className="text-sm text-gray-500">{city.desc}</div>
                    </div>
                  </button>
                ))}
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

export default MobileSearchModal;
